/**
 * Per-child colour coding (task 5.2b).
 *
 * A parent looking at two or three children side by side needs to know which
 * column, which filter button and which badge belong to which child without
 * re-reading the name every time. That only works if a child's colour is the
 * SAME everywhere it appears, so this is the one place a child colour is
 * decided.
 *
 * WHY THE COLOUR IS KEYED ON POSITION, NOT ON THE CHILD'S ID
 * The obvious alternative -- hash the canvasUserId into the palette -- gives
 * a stable colour per child but an arbitrary one, so two siblings can hash to
 * neighbouring hues and a parent with three children can get three colours
 * that are hard to tell apart. Keying on the child's index in
 * getCanonicalChildOrder()'s output instead guarantees the first N children
 * get the first N palette entries, which are hand-picked to be mutually
 * distinguishable. It is equally stable, because the canonical order itself
 * is stable (availableUsers order, unsorted -- see
 * hooks/useChildrenCourseData.ts), and it is what makes "consistent across
 * every view" achievable: every view derives the colour from the same
 * canonical list, so they cannot disagree.
 *
 * The palette deliberately avoids TIER_META's red/orange/blue
 * (utils/priorityUtils.js), because tier dots and child dots appear on the
 * same screen and a child accidentally coloured "critical red" reads as a
 * severity signal.
 */

export const CHILD_COLORS: string[] = [
  '#7b5cd6', // violet
  '#0f9b8e', // teal
  '#c2185b', // magenta
  '#5b7c99', // slate blue
  '#8d6e2f', // bronze
  '#4a7c3f', // moss
];

/** Fallback for a child that is not in the canonical list at all. */
export const CHILD_COLOR_FALLBACK = '#6b7280';

/** An entry from the canonical child order -- only `id` is read here. */
export interface CanonicalChild {
  id: string | number;
}

/**
 * The colour for one child, resolved by that child's position in the
 * canonical order.
 *
 * @param {Array<{id: string}>} children - canonical order, as returned by
 *   getCanonicalChildOrder / useChildrenCourseData().children.
 * @param {string|number} childUserId - the child to colour.
 * @returns {string} a CSS colour.
 */
export const getChildColor = (
  children: Array<CanonicalChild | null | undefined> | null | undefined,
  childUserId: string | number | null | undefined
): string => {
  if (!Array.isArray(children) || childUserId === null || childUserId === undefined) {
    return CHILD_COLOR_FALLBACK;
  }
  const wanted = String(childUserId);
  const index = children.findIndex((child) => child && String(child.id) === wanted);
  if (index < 0) return CHILD_COLOR_FALLBACK;
  return CHILD_COLORS[index % CHILD_COLORS.length];
};
